import { useState, useRef, useEffect } from "react";
import { Sparkles, Loader2, RefreshCw, Newspaper, DollarSign, Target, Users, Send, MessageSquare } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = process.env.REACT_APP_BACKEND_URL;

const MARKDOWN_COMPONENTS = {
  h3: ({ children }) => <h3 className="text-base font-bold text-white mb-2 mt-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-bold text-cyan-300 mb-1 mt-2">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-slate-300 leading-relaxed mb-2">{children}</p>,
  ul: ({ children }) => <ul className="text-sm text-slate-300 space-y-1 ml-3 mb-2 list-disc list-inside">{children}</ul>,
  ol: ({ children }) => <ol className="text-sm text-slate-300 space-y-1 ml-3 mb-2 list-decimal list-inside">{children}</ol>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded-lg border border-slate-700">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-800/60">{children}</thead>,
  th: ({ children }) => <th className="px-2 py-1.5 text-left text-slate-300 font-semibold border-b border-slate-700">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1.5 text-slate-300 border-b border-slate-800/50">{children}</td>,
};

function Section({ icon: Icon, title, content, color }) {
  return (
    <div className={`rounded-xl border ${color} overflow-hidden bg-slate-900/60`}>
      <div className={`px-4 py-2.5 border-b ${color} flex items-center gap-2`}>
        <Icon size={16} className="text-white" />
        <h3 className="text-sm font-bold text-white">{title}</h3>
      </div>
      <div className="p-4">
        {content ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
            {content}
          </ReactMarkdown>
        ) : (
          <div className="text-slate-500 text-xs italic">Aucune donnée</div>
        )}
      </div>
    </div>
  );
}

function ChatBox({ ticker, disabled }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setError(null);
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/smart-analyzer/${encodeURIComponent(ticker)}/chat`,
        { message: q, history: messages },
        { timeout: 90000 }
      );
      setMessages([...next, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    "Que feras-tu si l'action casse le stop ?",
    "Compare-la à un concurrent direct",
    "Quel est le risque #1 sur 6 mois ?",
    "Que pense le management du Q prochain ?",
  ];

  return (
    <div className="rounded-xl border border-violet-500/30 overflow-hidden bg-slate-900/60 mt-2">
      <div className="px-4 py-2.5 border-b border-violet-500/30 flex items-center gap-2 bg-violet-500/10">
        <MessageSquare size={16} className="text-violet-300" />
        <h3 className="text-sm font-bold text-white">Chat avec Claude sur {ticker}</h3>
      </div>
      <div className="p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-xs text-slate-500 italic px-1 pb-1">
            Pose une question de suivi sur le ticker — Claude garde toute l&apos;analyse en contexte.
          </div>
        )}
        {messages.length > 0 && (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`rounded-lg px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-cyan-500/10 border border-cyan-500/20 text-slate-100 ml-6"
                    : "bg-slate-800/60 border border-slate-700 text-slate-200 mr-6"
                }`}
                data-testid={`chat-msg-${i}`}
              >
                {m.role === "user" ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                    {m.content}
                  </ReactMarkdown>
                )}
              </div>
            ))}
            {loading && (
              <div className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 mr-6 flex items-center gap-2 text-sm text-slate-400">
                <Loader2 size={14} className="animate-spin text-violet-300" />
                Claude réfléchit...
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}

        {error && (
          <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
            {error}
          </div>
        )}

        {/* Quick suggestions */}
        {messages.length === 0 && !disabled && (
          <div className="flex flex-wrap gap-1.5">
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => setInput(q)}
                className="text-[11px] px-2 py-1 bg-slate-800/60 hover:bg-violet-500/15 border border-slate-700 hover:border-violet-400/40 text-slate-300 hover:text-violet-200 rounded transition"
                data-testid={`chat-quick-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            disabled={disabled || loading}
            placeholder={`Une question sur ${ticker} ?`}
            className="flex-1 px-3 py-2.5 bg-slate-900/70 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-400/60 disabled:opacity-50"
            data-testid="chat-input"
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading || disabled}
            className="px-4 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold text-sm rounded-lg flex items-center gap-2 disabled:opacity-40"
            data-testid="chat-send"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SmartAnalyzer({ ticker, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mgmtLoading, setMgmtLoading] = useState(false);

  const fetchManagement = async (refresh = false) => {
    if (!ticker) return;
    setMgmtLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/smart-analyzer/${encodeURIComponent(ticker)}/management`,
        { params: refresh ? { refresh: true } : {}, timeout: 90000 }
      );
      setData((prev) => prev ? { ...prev, management: r.data.management } : prev);
    } catch (e) {
      console.error("Management analysis error", e);
    } finally {
      setMgmtLoading(false);
    }
  };

  const run = async (refresh = false) => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/smart-analyzer/${encodeURIComponent(ticker)}`, {
        params: refresh ? { refresh: true } : {},
        timeout: 90000,
      });
      setData(r.data);
      // Auto-fetch management section asynchronously if missing
      if (!r.data.management) {
        fetchManagement(false);
      }
    } catch (e) {
      console.error("Smart analyzer error", e);
      setError(e.response?.data?.detail || e.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  if (!ticker) return null;

  return (
    <div className="glass-card rounded-2xl border border-purple-500/30 overflow-hidden" data-testid="smart-analyzer-panel">
      <div className="bg-gradient-to-r from-purple-600/20 via-fuchsia-600/15 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-400/40">
            <Sparkles size={18} className="text-purple-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">
              Smart Analysis — <span className="text-purple-300">{ticker}</span>
            </h3>
            <p className="text-slate-400 text-xs">
              Framework Claude 4-prompts : Narrative · Fundamentals · Asymmetry · Management
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <button
              onClick={() => run(true)}
              disabled={loading}
              className="p-2 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 transition disabled:opacity-50"
              data-testid="smart-analyzer-refresh-btn"
              title="Forcer la régénération"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 text-xs transition"
              data-testid="smart-analyzer-close-btn"
            >
              Fermer
            </button>
          )}
        </div>
      </div>

      {!data && !loading && !error && (
        <div className="p-6 text-center">
          <button
            onClick={() => run(false)}
            className="px-5 py-2.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/40 text-purple-200 font-semibold text-sm transition"
            data-testid="smart-analyzer-run-btn"
          >
            Lancer l&apos;analyse profonde de {ticker}
          </button>
          <p className="text-slate-500 text-xs mt-3">
            ~40 secondes via Claude Sonnet 4.5. Cache 30 min.
          </p>
        </div>
      )}

      {loading && (
        <div className="p-6 text-center">
          <Loader2 size={24} className="animate-spin text-purple-300 mx-auto mb-2" />
          <p className="text-slate-300 text-sm">Claude analyse {ticker}…</p>
          <p className="text-slate-500 text-xs mt-1">4 prompts en parallèle (~40s)</p>
        </div>
      )}

      {error && (
        <div className="p-6 text-center">
          <p className="text-rose-400 text-sm">Erreur : {error}</p>
          <button
            onClick={() => run(false)}
            className="mt-3 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs"
          >
            Réessayer
          </button>
        </div>
      )}

      {data && (
        <div className="p-4 space-y-3" data-testid="smart-analyzer-content">
          {/* Snapshot pills */}
          {data.snapshot && (
            <div className="flex flex-wrap gap-2 pb-2 border-b border-slate-700/50">
              {data.snapshot.current_price && (
                <span className="text-xs px-2 py-1 rounded bg-slate-800/60 border border-slate-700 text-slate-300">
                  ${data.snapshot.current_price}
                </span>
              )}
              {data.snapshot.market_cap && (
                <span className="text-xs px-2 py-1 rounded bg-slate-800/60 border border-slate-700 text-slate-300">
                  MCap ${(data.snapshot.market_cap / 1e9).toFixed(2)}B
                </span>
              )}
              {data.snapshot.forward_pe && (
                <span className="text-xs px-2 py-1 rounded bg-slate-800/60 border border-slate-700 text-slate-300">
                  Fwd PE {data.snapshot.forward_pe.toFixed(1)}
                </span>
              )}
              {data.snapshot.perf_30d != null && (
                <span className={`text-xs px-2 py-1 rounded border ${data.snapshot.perf_30d >= 0 ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`}>
                  30D {data.snapshot.perf_30d >= 0 ? "+" : ""}{data.snapshot.perf_30d.toFixed(1)}%
                </span>
              )}
              {data.snapshot.fifty_two_week_low && (
                <span
                  className="text-xs px-2 py-1 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300"
                  title={data.snapshot.perf_from_52w_low != null ? `+${data.snapshot.perf_from_52w_low.toFixed(1)}% depuis le plus bas 52 semaines` : "Plus bas 52 semaines"}
                >
                  52w Low ${data.snapshot.fifty_two_week_low.toFixed(2)}
                  {data.snapshot.perf_from_52w_low != null && (
                    <span className="ml-1 text-emerald-300">
                      (+{data.snapshot.perf_from_52w_low.toFixed(0)}%)
                    </span>
                  )}
                </span>
              )}
              {data.snapshot.fifty_two_week_high && (
                <span
                  className="text-xs px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
                  title={data.snapshot.drawdown_52w_high != null ? `${data.snapshot.drawdown_52w_high.toFixed(1)}% vs plus haut 52 semaines` : "Plus haut 52 semaines"}
                >
                  52w High ${data.snapshot.fifty_two_week_high.toFixed(2)}
                  {data.snapshot.drawdown_52w_high != null && (
                    <span className={`ml-1 ${data.snapshot.drawdown_52w_high >= -5 ? "text-emerald-200" : "text-amber-300"}`}>
                      ({data.snapshot.drawdown_52w_high.toFixed(1)}%)
                    </span>
                  )}
                </span>
              )}
              {data.snapshot.ath && (
                <span
                  className="text-xs px-2 py-1 rounded bg-purple-500/10 border border-purple-500/30 text-purple-200"
                  title={data.snapshot.ath_date ? `ATH atteint le ${data.snapshot.ath_date}` : "All-Time High"}
                >
                  ATH ${data.snapshot.ath.toFixed(2)}
                  {data.snapshot.drawdown_true_ath != null && (
                    <span className={`ml-1 ${data.snapshot.drawdown_true_ath >= -5 ? "text-emerald-200" : data.snapshot.drawdown_true_ath >= -20 ? "text-amber-300" : "text-rose-300"}`}>
                      ({data.snapshot.drawdown_true_ath.toFixed(1)}%)
                    </span>
                  )}
                </span>
              )}
              {data.snapshot.data_source === "quote_cache" && (
                <span className="text-xs px-2 py-1 rounded bg-slate-700/40 border border-slate-600/50 text-slate-400" title="yfinance rate limited - fallback cache">
                  cache prix
                </span>
              )}
              {data.cached && (
                <span className="text-xs px-2 py-1 rounded bg-slate-700/40 border border-slate-600/50 text-slate-400">
                  cache {data.cache_age_seconds}s
                </span>
              )}
            </div>
          )}

          <Section icon={Newspaper} title="Narrative" content={data.narrative} color="border-cyan-500/30" />
          <Section icon={DollarSign} title="Fundamentals" content={data.fundamentals} color="border-emerald-500/30" />
          <Section icon={Target} title="Asymmetry" content={data.asymmetry} color="border-amber-500/30" />

          {/* Management: lazy-loaded after main 3 sections */}
          {data.management ? (
            <Section icon={Users} title="Management" content={data.management} color="border-violet-500/30" />
          ) : (
            <div className="rounded-xl border border-violet-500/30 overflow-hidden bg-slate-900/60">
              <div className="px-4 py-2.5 border-b border-violet-500/30 flex items-center justify-between bg-violet-500/5">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-white" />
                  <h3 className="text-sm font-bold text-white">Management</h3>
                </div>
                {mgmtLoading ? (
                  <span className="flex items-center gap-1.5 text-xs text-violet-300">
                    <Loader2 size={12} className="animate-spin" /> Claude rédige…
                  </span>
                ) : (
                  <button
                    onClick={() => fetchManagement(false)}
                    className="text-xs px-2.5 py-1 bg-violet-500/20 hover:bg-violet-500/30 border border-violet-400/40 text-violet-200 rounded transition"
                    data-testid="smart-analyzer-mgmt-btn"
                  >
                    Charger l&apos;analyse management
                  </button>
                )}
              </div>
              <div className="p-4 text-xs text-slate-500 italic">
                Analyse CEO/CFO + sentiment market sur le leadership (chargée séparément ~30s).
              </div>
            </div>
          )}

          {/* Follow-up chat */}
          <ChatBox ticker={ticker} disabled={loading} />

          <p className="text-[10px] text-slate-600 text-center pt-1">
            Généré par {data.model || "Claude"} · {data.fetched_at ? new Date(data.fetched_at).toLocaleString("fr-FR") : ""}
          </p>
        </div>
      )}
    </div>
  );
}
